import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ChangableComponent } from '@app/models/changable.component';
import { LibraryRouter } from '@app/models/library.router';
import { IFilter } from '@app/models/lingo2-models/content';
import { CLibraryFilterData } from '@app/store/models';
import { getCurrentItem, getUserServiceCategories } from '@app/store/reducers/content.reducer';
import {
  AccountService,
  ConfigService,
  ContentService,
  ContextService,
  MeetingsService,
  UserServicesService,
} from '@core/services';
import { Store } from '@ngrx/store';
import { getLibraryRouteData } from '@store/reducers/library.reducer';
import { getMyProfile } from '@store/reducers/profile.reducer';
import {
  ContentTypeEnum,
  EContentPanelRoute,
  IBasicContent,
  Language,
  MicroText,
  Subject as BaseSubject,
  SubjectsRegistry,
  UserProfile,
  UserServiceCategory,
} from 'lingo2-models';
import { uniqBy } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../../i18n/translate.service';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BreadcrumbsComponent extends ChangableComponent implements OnInit {
  private contextService = inject(ContextService);
  protected contentService = inject(ContentService);
  protected accountService = inject(AccountService);
  protected meetingService = inject(MeetingsService);
  protected userServices = inject(UserServicesService);
  protected router = inject(Router);
  protected readonly store = inject(Store);
  private translate = inject(TranslateService);
  private configService = inject(ConfigService);
  deviceService = inject(DeviceDetectorService);

  @Input() displayMode: 'default' | 'breadCrumbsOnly' | 'filterOnly' = 'default';
  @Input() pageType: 'default' | 'content' = 'default';
  public breadCrumbs: Array<{ title: string; link?: string }> = [];
  public filterList: IFilter[] = [];
  public isFilterListVisible = false;

  private _libraryFilterData: CLibraryFilterData;
  private _subjectsRegistry: SubjectsRegistry;
  private _languages: Language[];
  private _userServiceCategories: UserServiceCategory[];
  private _currentItem: IBasicContent;
  private _userProfile: UserProfile;

  private libRouter = new LibraryRouter();

  public ngOnInit(): void {
    combineLatest([
      this.store.select(getLibraryRouteData).pipe(filter((v) => !!v)),
      this.configService.subjectsV2$.pipe(filter((v) => !!v)),
      this.store.select(getCurrentItem),
      this.configService.languages$.pipe(filter((v) => !!v)),
      this.store.select(getUserServiceCategories).pipe(filter((v) => !!v)),
      this.store.select(getMyProfile).pipe(filter((v) => !!v)),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([libraryFilterData, subjectsRegistry, currentItem, languages, userServiceCategories, profile]) => {
          this._libraryFilterData = libraryFilterData;
          this._subjectsRegistry = subjectsRegistry;
          this._currentItem = currentItem;
          this._languages = languages;
          this._userServiceCategories = userServiceCategories;
          this._userProfile = profile;
          this.isFilterListVisible = !libraryFilterData?.item;
          this.libRouter.setLibraryFilterData(libraryFilterData);
          this.generateList();
          this.generateFilterList();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public isMicroText() {
    return (this._currentItem as MicroText)?.type === ContentTypeEnum.microtext;
  }

  public onCleanFilter(filterItem: IFilter) {
    this.libRouter.setBaseRoute(this._libraryFilterData.baseRoute);
    switch (filterItem.type) {
      case 'subject':
        this.libRouter.removeSubject(filterItem?.code);
        break;
      case 'contentType':
        this.libRouter.removeContentTypeById(filterItem?.code);
        break;
      case 'language':
        this.libRouter.removeLanguage(filterItem?.code);
        break;
    }
    this.onApplyFilter();
    this.detectChanges();
  }

  public onApplyFilter() {
    const link = this.libRouter.getLink();
    this.router.navigate([link.path], { queryParams: link.parameters }).then();
  }

  public getLabelTypeFilter(filterItem: IFilter): string {
    switch (filterItem.type) {
      case 'subject':
        return 'library.breadcrumbs.subject';
      case 'contentType':
        return 'library.breadcrumbs.content-type';
      case 'language':
        return 'library.breadcrumbs.language';
    }
  }

  private generateFilterList() {
    this.filterList = [
      ...this.findSubjectsByCode(this._libraryFilterData.subjectCodes).map((_s) => ({
        title: _s.title,
        code: _s.code,
        type: 'subject',
      })),
      ...LibraryRouter.convertContentTypesToFilter(this._libraryFilterData.contentTypeCodes).map((_f) => ({
        ..._f,
        title: this.translate.directInstant(_f.title),
      })),
      ...this.findLanguageByCode(this._libraryFilterData.languageCodes).map((_l) => ({
        title: _l.title,
        code: _l.code,
        type: 'language',
      })),
    ];
  }

  private generateList() {
    const generatedBreadCrumbs: Array<{ title: string; link?: string }> = [];

    if (this._libraryFilterData.item && this._currentItem) {
      generatedBreadCrumbs.push({
        title: this._currentItem.title,
      });
    }
    let router = this.libRouter;
    while (router) {
      let link = router.getLink().path;
      const subjectCodes = router.getSubjects();
      const contentTypeCodes = router.getContentTypes();
      const baseRoute = router.getBaseRoute();

      let titles = [];

      if (router.getBaseRoute() !== EContentPanelRoute.main) {
        /** Костыль для задачи https://app.clickup.com/t/2znqxd7 */
        /** @TODO: УБрать после создания детальной карточки классрума и настройки роута*/

        if (router.getBaseRoute().replace('/', '') === String(EContentPanelRoute.classrooms)) {
          titles = [`${baseRoute}.page-header-title`];
          link = `/${EContentPanelRoute['my-classes/classrooms']}`;
        } else {
          titles = [`${baseRoute}.page-header-title`];
        }
      } else {
        if (subjectCodes?.length > 0) {
          titles = this.findSubjectsByCode(subjectCodes).map((_s) => `${_s.title}`);
        }
      }

      if (contentTypeCodes?.length > 0) {
        switch (baseRoute) {
          case EContentPanelRoute.classes:
            titles = this.convertUserServiceCategoriesToTitles(contentTypeCodes);
            break;
          default:
            titles = LibraryRouter.convertContentTypesToFilter(contentTypeCodes).map((_s) => _s.title);
        }
      }

      titles = titles.filter((s) => (s || '').length);

      if (titles.length > 0) {
        generatedBreadCrumbs.push({
          title: Object.values(titles.map((t) => this.translate.directInstant(t))).join(', '),
          link,
        });
      }

      router = router.getUpRouter();
    }

    generatedBreadCrumbs.push({
      title: this.translate.directInstant('library.breadcrumbs.home'),
      link: '/main',
    });

    this.breadCrumbs = generatedBreadCrumbs.reverse();
    this.detectChanges();
  }

  private findSubjectsByCode(subjectsCodes: string[]): BaseSubject[] {
    return uniqBy(
      [
        ...this._subjectsRegistry.other,
        ...this._subjectsRegistry.user,
        ...this._subjectsRegistry.native,
        ...this._subjectsRegistry.foreign,
        ...this._subjectsRegistry.non_categorized,
        ...this._subjectsRegistry.recent,
      ].filter((_s) => !!subjectsCodes.find((_sc) => _sc === _s.code)),
      'id',
    );
  }

  private findSubjectsById(subjectsIds: number[]): BaseSubject[] {
    return uniqBy(
      [
        ...this._subjectsRegistry.other,
        ...this._subjectsRegistry.user,
        ...this._subjectsRegistry.native,
        ...this._subjectsRegistry.foreign,
        ...this._subjectsRegistry.non_categorized,
        ...this._subjectsRegistry.recent,
      ].filter((_s) => !!subjectsIds.find((_sc) => +_sc === +_s.id)),
      'id',
    );
  }

  private convertUserServiceCategoriesToTitles(userServiceCategories: string[]) {
    return userServiceCategories.map((category) => this._userServiceCategories.find((c) => c.code === category)?.title);
  }

  private findLanguageByCode(languagesCodes: string[]): Language[] {
    const result = this._languages.filter((_l) => !!languagesCodes.find((_lc) => _lc === _l.code), 'id');
    return this.smartSortLanguages(result);
  }

  private smartSortLanguages(languages: Language[]): Language[] {
    if (!this._userProfile) {
      return languages;
    }
    const spokenLanguages = languages.filter(
      (_l) => !!this._userProfile.spoken_languages.find((_sl) => +_sl.language_id === +_l.id),
    );
    const teachingLanguageSubjects = this.findSubjectsById(
      this._userProfile.teaching_subjects.map((_s) => _s.subject_id),
    ).filter((_ts) => !!_ts.language_id);
    const teachingLanguages = languages.filter(
      (_tl) => !!teachingLanguageSubjects.find((_tls) => +_tls.language_id === +_tl.id),
    );
    const othersLanguages = languages.filter(
      (_ol) => ![...spokenLanguages, ...teachingLanguages].find((_l) => +_l.id === +_ol.id),
    );
    return uniqBy([...spokenLanguages, ...teachingLanguages, ...othersLanguages], 'id');
  }

  public get breadcrumbsClasses() {
    const classes = [];
    if (this.pageType) {
      classes.push(this.pageType);
    }
    return classes;
  }

  public trackByFn(index) {
    return index;
  }
}
